import type { MechanicDefinition } from "./types";

/** D&D 5e class → saving throw proficiency mapping */
// ⭐ auditPasses: 1 — applied at definition below

const CLASS_SAVE_PROFICIENCIES: Record<string, string[]> = {
  barbarian: ["STR", "CON"],
  bard: ["DEX", "CHA"],
  cleric: ["WIS", "CHA"],
  druid: ["INT", "WIS"],
  fighter: ["STR", "CON"],
  monk: ["STR", "DEX"],
  paladin: ["WIS", "CHA"],
  ranger: ["STR", "DEX"],
  rogue: ["DEX", "INT"],
  sorcerer: ["CON", "CHA"],
  warlock: ["WIS", "CHA"],
  wizard: ["INT", "WIS"],
  artificer: ["CON", "INT"],
};

export function getProficiencyBonus(level: number): number {
  if (level <= 4) return 2;
  if (level <= 8) return 3;
  if (level <= 12) return 4;
  if (level <= 16) return 5;
  return 6;
}

export const savingThrows: MechanicDefinition = {
  id: "saving_throws",
  disabled: true,
  name: "Saving Throws",
  description: "D&D-style saving throws with class proficiencies and proficiency bonus scaling",
  icon: "ShieldCheck",
  category: "systems",
  auditPasses: 1, // ⭐
  aiHints: {
    purpose: "Adds saving throw mechanics — resist spells, traps, and effects with ability-based saves.",
    whenToUse: "D&D-style games with combat and magic. Pairs tightly with conditions and combat.",
    synergies: ["combat", "conditions", "skill_checks", "character_classes"],
    conflicts: [],
    prerequisites: ["skill_checks should be active for stat definitions"],
    tags: ["saves", "saving-throws", "defense", "d&d", "resistance"],
    complexity: 2,
    exampleConfig: { use_class_proficiencies: true },
    stateVariables: ["saving_throw_proficiencies", "proficiency_bonus", "spell_save_dc", "_last_save_roll", "_last_save_success"],
  },
  configSchema: [
    {
      key: "proficiency_source",
      label: "Save Proficiency Source",
      type: "select",
      options: [
        { value: "class_based", label: "Class-Based (auto)", description: "Automatically grant save proficiencies based on character class" },
        { value: "manual", label: "Manual Selection", description: "Player chooses 2 saving throw proficiencies" },
        { value: "all_proficient", label: "All Proficient", description: "Proficiency in all saves (heroic mode)" },
      ],
      default: "class_based",
    },
  ],
  systemPromptSection: (config) => {
    const source = config.proficiency_source || "class_based";

    const classMap = Object.entries(CLASS_SAVE_PROFICIENCIES)
      .map(([cls, saves]) => `  ${cls}: ${saves.join(", ")}`)
      .join("\n");

    return `## SAVING THROWS (Hard Mechanic)
- Proficiency source: ${source}
- Use saving_throw tool for ALL saving throws (vs spells, traps, effects, concentration).
- Roll: 1d20 + ability modifier + proficiency bonus (if proficient in that save).
- Proficiency bonus: +2 (L1-4), +3 (L5-8), +4 (L9-12), +5 (L13-16), +6 (L17-20).

### SPELL SAVE DC
- When a caster forces a save: DC = 8 + proficiency bonus + spellcasting ability modifier.
- Track spell_save_dc in state.

### CLASS SAVE PROFICIENCIES
${source === "class_based" ? classMap : source === "all_proficient" ? "  All saves are proficient." : "  Player selects 2 saving throw proficiencies."}

### COMMON SAVE TRIGGERS
- STR: Resisting being pushed, grappled, or physically restrained.
- DEX: Dodging AoE (fireball, traps). On success, often half damage.
- CON: Resisting poison, disease, concentration checks, death saves.
- INT: Resisting mental illusions, psychic effects.
- WIS: Resisting charm, fear, mind control. Most common spell save.
- CHA: Resisting banishment, possession, soul effects.

### CONCENTRATION SAVES
- When a concentrating caster takes damage: CON save DC = max(10, floor(damage/2)).
- On failure, the concentration spell ends. Use remove_condition to clear.`;
  },
  initializeState: (config) => ({
    saving_throw_proficiencies: [],
    proficiency_bonus: 2,
    spell_save_dc: 13,
  }),
  onEvent: (event, context) => {
    // React to level_up to auto-scale proficiency bonus
    if (event.type === "level_up") {
      const newLevel = event.data.new_level || 1;
      const newBonus = getProficiencyBonus(newLevel);
      const oldBonus = context.state.variables?.proficiency_bonus || 2;
      if (newBonus !== oldBonus) {
        return {
          stateUpdates: { proficiency_bonus: newBonus },
          narrativeNotes: [`📈 Proficiency bonus increased: +${oldBonus} → +${newBonus}`],
        };
      }
    }
    return null;
  },
  tools: [
    {
      type: "function",
      function: {
        name: "saving_throw",
        description: "Roll a saving throw. 1d20 + ability mod + proficiency (if proficient). Compare vs DC.",
        parameters: {
          type: "object",
          properties: {
            target: { type: "string", description: "Entity name or 'player'" },
            ability: { type: "string", description: "Saving throw ability (STR, DEX, CON, INT, WIS, CHA)" },
            dc: { type: "number", description: "Difficulty class to beat" },
            advantage: { type: "boolean", description: "Roll with advantage" },
            disadvantage: { type: "boolean", description: "Roll with disadvantage" },
            source: { type: "string", description: "What triggered the save (spell name, trap, effect)" },
            half_on_success: { type: "boolean", description: "Target takes half damage on success (e.g. Fireball)" },
          },
          required: ["target", "ability", "dc"],
        },
      },
    },
  ],
};
